import debounce from 'lodash/debounce'
import isEqual from 'lodash/isEqual'

import FluxComposerStore from '~publish/legacy/composer/composer/stores/ComposerStore'

import type { ComposerStore } from '../composerStore'
import { checkOmniParityAfterMirror } from './omniDrift'
import { isMirrorWriteInProgress, runMirrorWrite } from './mirrorGate'
import { projectFluxOmniAssets } from './projectFluxOmniContent'
import type { OmniContentProjection } from './projectFluxOmniContent'

/**
 * Editor-state updates emit per keystroke, and the asset projection deep-clones
 * every attached media object — so a typing burst with media attached would
 * otherwise clone the whole attachment set on each keystroke. The wait is short
 * enough that a media change lands well before a user can reach the save
 * button; `maxWait` bounds staleness under continuous emission (e.g. an AI
 * stream inserting text).
 */
const ASSET_MIRROR_DEBOUNCE_MS = 150
const ASSET_MIRROR_MAX_WAIT_MS = 1000

export interface FluxToZustandMirror {
  unsubscribe: () => void
  /**
   * Apply any pending (debounced) asset pass immediately. Call before reading
   * `omni.assets` at a correctness boundary (save mapping, parity assertions).
   */
  flush: () => void
}

/**
 * Register a Flux ComposerStore change listener that mirrors the omni draft
 * into Zustand's `postSlice.omni`.
 *
 * Design: subscribes to `ComposerStore.addChangeListener` (store-level) rather
 * than intercepting individual action types. Some Flux paths — `copyDraftContents()`
 * on return-to-omni and synchronously dispatched editor-state updates — mutate drafts
 * directly then emit, so per-action interception would miss them. The store
 * always calls `emitChange()` after every mutation, so snapshot-diff-on-emit
 * catches everything reliably.
 *
 * The mirror is split by cost, not by concept:
 * - Text family (`text`, `urls`, `unshortenedUrls`) and `aiAssisted` sync on
 *   every emission — plain string/small-array compares. Keeping these
 *   synchronous is a correctness requirement, not an optimisation: text flows
 *   editor→Zustand first (via the text-sync plugin), so a deferred Flux→Zustand
 *   text pass could apply stale Flux text over a newer keystroke.
 * - `assets` sync through a leading+trailing debounce. Assets currently flow
 *   one-way (Flux→Zustand — no Zustand asset writer exists), so a deferred
 *   pass can never fight a newer Zustand-side asset write.
 *
 * Field-level diffing keeps immer churn and devtools noise proportional to
 * actual changes and makes the mirror idempotent: the text-flow double-write
 * (editor state then COMPOSER_UPDATE_POST_TEXT) produces zero net change on
 * the second emission.
 *
 * The parity check runs at the end of each settled asset pass — the one moment
 * every mirrored field is expected to agree.
 */
export function installFluxToZustandMirror(
  store: ComposerStore,
): FluxToZustandMirror {
  // Stamped once per mirror install, so a drift report can tell how long this
  // composer session had been running — see `checkOmniParityAfterMirror`.
  // `performance.now()`, not `Date.now()`, since the wall clock can jump
  // (system time adjustment, NTP sync) across a session long enough to drift.
  const installedAt = performance.now()

  const syncTextFamily = (): void => {
    const fluxOmni = FluxComposerStore.getDraft('omni')
    if (!fluxOmni) return

    const current = store.getState().omni

    // Collect only fields that actually changed so immer does not produce
    // new references for unchanged data. Each field is checked individually
    // to keep the partial typed without casts.
    const partial: Partial<OmniContentProjection> = {}
    if (fluxOmni.text !== current.text) partial.text = fluxOmni.text
    if (!isEqual(fluxOmni.urls, current.urls)) partial.urls = [...fluxOmni.urls]
    if (!isEqual(fluxOmni.unshortenedUrls, current.unshortenedUrls))
      partial.unshortenedUrls = [...fluxOmni.unshortenedUrls]
    if (fluxOmni.aiAssisted !== current.aiAssisted)
      partial.aiAssisted = fluxOmni.aiAssisted

    if (Object.keys(partial).length > 0) {
      runMirrorWrite(() => {
        store.getState().syncOmniFromFlux(partial)
      })
    }
  }

  const syncAssets = (): void => {
    const fluxOmni = FluxComposerStore.getDraft('omni')
    if (!fluxOmni) return

    const projectedAssets = projectFluxOmniAssets(fluxOmni)
    if (!isEqual(projectedAssets, store.getState().omni.assets)) {
      runMirrorWrite(() => {
        store.getState().syncOmniFromFlux({ assets: projectedAssets })
      })
    }
    checkOmniParityAfterMirror(
      store,
      'fluxToZustand',
      performance.now() - installedAt,
    )
  }

  const debouncedSyncAssets = debounce(syncAssets, ASSET_MIRROR_DEBOUNCE_MS, {
    leading: true,
    trailing: true,
    maxWait: ASSET_MIRROR_MAX_WAIT_MS,
  })

  const handleChange = (): void => {
    if (isMirrorWriteInProgress()) return
    syncTextFamily()
    debouncedSyncAssets()
  }

  FluxComposerStore.addChangeListener(handleChange)

  return {
    flush: (): void => {
      debouncedSyncAssets.flush()
    },
    unsubscribe: (): void => {
      FluxComposerStore.removeChangeListener(handleChange)
      // Apply (not cancel) any pending pass so the last Flux state before
      // teardown is not lost to Zustand readers.
      debouncedSyncAssets.flush()
    },
  }
}
